import Phaser from 'phaser';
import type { ConnectionStatus } from '../../store/Store.js';
import { COLORS, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH, LAYOUT } from '../PhaserGame.js';

/**
 * Live multiplayer connection indicator. Lives in the bottom-right
 * corner of the map zone in MP only — single-player has no live
 * channel to surface, and that store reports 'connected' forever
 * so a chip there would be pure noise.
 *
 * Visual treatment matches the user's stated preference: subtle
 * when healthy (barely-there green dot), loud when there's a
 * problem (bright amber dot + "RECONNECTING…" label, pulsing).
 *
 * Previously lived at the top-right of the HUD; relocated to the
 * map's bottom-right so the header band is free for the overlay
 * Back button.
 */

const DOT_RADIUS = 5;
// DOT_X / DOT_Y must NOT be computed at module-load time — this
// view is imported transitively by PhaserGame.ts (via GameScene),
// and the LAYOUT constant lives in PhaserGame, so reading it here
// at module scope creates a circular-import temporal dead zone
// (the page fails to load with "Cannot read properties of undefined").
// Compute inside the constructor instead.
const LABEL_GAP = 10; // space between dot and "RECONNECTING…"
const LABEL_FONT_SIZE = '14px';
const CONNECTED_ALPHA = 0.45;

export class ConnectionStatusChip {
  private readonly scene: Phaser.Scene;
  private readonly dot: Phaser.GameObjects.Arc;
  private readonly label: Phaser.GameObjects.Text;
  // Explicit `| undefined` (not `?:`) so we can reassign to undefined
  // under exactOptionalPropertyTypes — the chip toggles the pulse on
  // and off as the status changes.
  private pulse: Phaser.Tweens.Tween | undefined = undefined;
  private destroyed = false;

  constructor(scene: Phaser.Scene, initial: ConnectionStatus) {
    this.scene = scene;

    // Compute placement here (not at module scope) to dodge the
    // PhaserGame circular-import TDZ — see the comment block above
    // the LABEL_GAP constant.
    const mapBottomY =
      DESIGN_HEIGHT -
      LAYOUT.diceTrayHeight -
      LAYOUT.actionHeight -
      LAYOUT.navHeight -
      LAYOUT.bottomSafe;
    const dotX = DESIGN_WIDTH - 24;
    const dotY = mapBottomY - 24;

    this.dot = scene.add
      .circle(dotX, dotY, DOT_RADIUS, COLORS.accentGreenInt)
      .setDepth(50);

    // Right-anchored so the label grows leftward from the dot
    // without ever colliding with the canvas right edge.
    this.label = scene.add
      .text(dotX - DOT_RADIUS - LABEL_GAP, dotY, 'RECONNECTING…', {
        fontFamily: FONT_MONO,
        fontSize: LABEL_FONT_SIZE,
        color: COLORS.accentAmberHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(1, 0.5)
      .setDepth(50);

    this.setStatus(initial);
  }

  setStatus(status: ConnectionStatus): void {
    if (this.destroyed) return;
    if (status === 'connected') {
      this.dot.setFillStyle(COLORS.accentGreenInt);
      this.dot.setAlpha(CONNECTED_ALPHA);
      this.label.setVisible(false);
      this.stopPulse();
    } else {
      this.dot.setFillStyle(COLORS.accentAmberInt);
      this.dot.setAlpha(1);
      this.label.setVisible(true);
      this.startPulse();
    }
  }

  private startPulse(): void {
    if (this.pulse) return;
    this.pulse = this.scene.tweens.add({
      targets: this.dot,
      alpha: { from: 1, to: 0.35 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private stopPulse(): void {
    this.pulse?.stop();
    this.pulse = undefined;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopPulse();
    this.dot.destroy();
    this.label.destroy();
  }
}
